import { memo } from "react";
import { formatTimestamp } from "../../../../../../../utils";
import styles from "./RenderGeneralDetails.module.scss";
import {
	ServiceRequestInstance,
	ServiceRequestPaymentStatusMap,
	ServiceRequestStatus,
	ServiceRequestStatusMap,
} from "../../../../../../../model/service-request/service-request-instance";
import { FieldType } from "../../../../../../../model/service-request/attribute";

type Props = {
	sectionInstance: ServiceRequestInstance;
	status: ServiceRequestStatus | undefined;
};

const RenderGeneralDetails: React.FC<Props> = memo((props: Props) => {
	const data = props.sectionInstance;

	const statusClassMap: Record<string, string> = {
		Unknown: styles.circleBlack,
		Free: styles.circleGray,
		Unpaid: styles.circleRed,
		Paid: styles.circleGreen,
		Completed: styles.circleGreen,
		InProgress: styles.circleYellow,
		Assigned: styles.circleBlack,
		Unassigned: styles.circleRed,
	};

	const getPriceAmount = () => {
		if (data?.serviceRequest?.price) return `${data.serviceRequest.price.currency} ${data.serviceRequest.price.amount}`;

		let currency: string | undefined;
		let totalAmount = 0;

		data?.sectionInstances?.forEach(si => {
			si.attributeValues?.forEach(av => {
				const fieldType = av.attributeDefinition?.fieldType;

				// Dropdown - sum first level option prices
				if (fieldType === FieldType.Dropdown) {
					const selectedOptions = av.attributeDefinition.childrenOptionDefinitions?.filter(option => av.selectedOptionIds.includes(option.id!)) || [];

					selectedOptions.forEach(option => {
						if (option.price) {
							if (!currency) currency = option.price.currency;
							totalAmount += option.price.amount;
						}
					});
				}

				// ConditionalDropdown - sum second level option prices
				if (fieldType === FieldType.ConditionalDropdown) {
					av.attributeDefinition.childrenOptionDefinitions?.forEach(option => {
						const selectedChildOptions = option.childOptions?.filter(childOption => av.selectedOptionIds.includes(childOption.id!)) || [];

						selectedChildOptions.forEach(childOption => {
							if (childOption.price) {
								if (!currency) currency = childOption.price.currency;
								totalAmount += childOption.price.amount;
							}
						});
					});
				}
			});
		});

		return totalAmount === 0 ? "/" : `${currency || "NGN"} ${totalAmount}`;
	};

	return (
		<div className={styles.generalDetailsContainer}>
			<span className={styles.sectionHeader}>General Details</span>
			<div className={styles.informationContainer}>
				<span>Date</span>
				<span>{formatTimestamp(data?.createdAt ?? "")}</span>
			</div>
			<div className={styles.informationContainer}>
				<span>Name of Customer</span>
				<span>
					{data?.customerFirstName} {data?.customerLastName}
				</span>
			</div>
			<div className={styles.informationContainer}>
				<span>Service Request Type</span>
				<span>{data?.serviceRequest.name}</span>
			</div>
			<div className={styles.informationContainer}>
				<span>Amount</span>
				<span>{getPriceAmount()}</span>
			</div>
			<div className={styles.informationContainer}>
				<span>Payment Status</span>
				<div className={styles.statusWrapper}>
					<div className={`${styles.circle} ${statusClassMap[ServiceRequestPaymentStatusMap[data?.paymentStatus ?? 0]]}`} />
					<span className={styles.ellipsisText}>{ServiceRequestPaymentStatusMap[data?.paymentStatus ?? 0]}</span>
				</div>
			</div>
			<div className={styles.informationContainer}>
				<span>Status</span>
				<div className={styles.statusWrapper}>
					{props.status && (
						<>
							<div className={`${styles.circle} ${statusClassMap[props.status!]}`} />
							<span className={styles.ellipsisText}>{props.status}</span>
						</>
					)}
				</div>
			</div>
		</div>
	);
});

export default RenderGeneralDetails;
